import _ from "lodash";
import React, { useState } from "react";
import { useAlert } from "react-alert";
import styled from "styled-components";
import strings from "Resources/strings";
import {
  Authorization,
  AuthorizationStatusEnum,
  IdentifierCaptureType,
  TerminalStatusUpdateType,
} from "@samacare/graphql";

import {
  useUpdateAuthorization,
  useUpdateAuthorizationStatus,
} from "../graphql/Authorization";
import { useCurrentAccount } from "../graphql/Account";
import { useCreateFollowUp } from "../graphql/FollowUp";
import BaseButton from "./BaseButton";
import { getNonApprovalTypesOptions } from "../util/nonApprovalTypes";
import Tooltip from "@samacare/design/core/Tooltip";
import { Autocomplete, Button, DatePicker, TextField } from "@samacare/design";
import { DialogModal } from "@@components/DialogModal";
import { useConfig } from "@@hooks/config";
import { useTheme } from "@samacare/design/core/styles";
import { PlacesType } from "react-tooltip";
import { useFeatureFlag } from "@@hooks/useFeatureFlag";
import { authorizationStatusDisplayName } from "../util/authUtils";

export interface UpdateAuthorizationStatusButtonProps {
  children: React.ReactNode;
  authorization: Authorization;
  defaultTo?: string;
  onUpdateSuccess?: () => void;
  styleOverrides?: object;
  tooltipPlace?: PlacesType | "top-start";
  disableTooltip?: boolean;
}

const FieldContainer = styled.div`
  margin-top: 16px;
`;

const ErrorMessage = styled.div`
  color: ${(props) => props.theme.red};
  text-align: center;
  margin-bottom: 20px;
  white-space: normal;
`;

type Option = {
  value: string;
  label: string;
};

export const UpdateAuthorizationStatusButton = ({
  children,
  authorization,
  defaultTo,
  onUpdateSuccess,
  styleOverrides,
  tooltipPlace,
  disableTooltip,
}: UpdateAuthorizationStatusButtonProps) => {
  const [selectedOption, setSelectedOption] = useState<Option>({
    value: "",
    label: "",
  });
  const [selectedFollowUpType, setSelectedFollowUpType] =
    useState<Option | null>(null);
  const [selectedNonApprovalType, setSelectedNonApprovalType] =
    useState<Option | null>(null);
  const [open, setOpen] = useState(false);
  const [portalAuthorizationId, setPortalAuthorizationId] =
    useState<string>("");
  const [responseDueBy, setResponseDueBy] = useState<Date | null>(null);
  const [nonApprovalInfo, setNonApprovalInfo] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [account] = useCurrentAccount();

  const originalOption = authorization.status;
  const originalPortalAuthorizationId =
    authorization.portalAuthorizationId ?? "";

  const config = useConfig();
  const theme = useTheme();
  const alert = useAlert();
  const STATUSES = config.CONSTANTS.AUTHORIZATION_STATUSES;

  const paBoostDrugs = useFeatureFlag<
    { drugName: string; drugOptionId: number }[]
  >(config.CONSTANTS.LAUNCH_DARKLY_FEATURE_FLAGS.PaBoost);

  const paBoostEnabled = _.some(
    paBoostDrugs ?? [],
    (drug: { drugName: string; drugOptionId: number }) =>
      drug.drugOptionId === authorization.DrugOptionId
  );

  const [updateAuthorizationStatus] = useUpdateAuthorizationStatus();
  const [updateAuthorization] = useUpdateAuthorization();
  const [createFollowUp] = useCreateFollowUp();

  const resetFields = () => {
    setResponseDueBy(null);
    setNonApprovalInfo("");
    setErrorMessage(null);
    setSelectedFollowUpType(null);
    setSelectedNonApprovalType(null);
  };

  const toggleModal = (e?: any) => {
    if (e) {
      e.stopPropagation();
    }

    const defaultSelected = defaultTo || authorization.status;
    setSelectedOption({
      value: defaultSelected,
      label: authorizationStatusDisplayName(defaultSelected),
    });
    setPortalAuthorizationId(authorization.portalAuthorizationId ?? "");
    resetFields();
    setOpen(!open);
  };

  const shouldDisplayNonApprovalReasons = () => {
    if (originalOption !== (selectedOption.value as AuthorizationStatusEnum)) {
      // Always display non-approval reasons for statuses listed in STATUSES_REQUIRING_NONAPPROVAL_REASON (e.g. Denied)
      // Only display non-approval reasons for Action Required if it's a Sama User.
      // Otherwise, user is correcting their own mistake and is not considered a non-approval
      return !!(
        _.includes(
          config.CONSTANTS.STATUSES_REQUIRING_NONAPPROVAL_REASON,
          selectedOption.value
        ) ||
        (_.includes([STATUSES.ACTION_REQUIRED], selectedOption.value) &&
          account?.isSamaUser)
      );
    }
    return false;
  };

  const shouldDisplayAdditionalComments = () => {
    return (
      (paBoostEnabled && account?.isSamaUser) ||
      (selectedNonApprovalType &&
        _.includes(
          config.CONSTANTS.NONAPPROVALS_REQUIRING_ADDITIONAL_INFO,
          selectedNonApprovalType.value
        ))
    );
  };

  const isAdditionalCommentsRequired =
    selectedNonApprovalType != null &&
    _.includes(
      config.CONSTANTS.NONAPPROVALS_REQUIRING_ADDITIONAL_INFO,
      selectedNonApprovalType.value
    );

  const getErrorMessage = () => {
    if (originalOption === (selectedOption.value as AuthorizationStatusEnum)) {
      return null;
    }

    if (shouldDisplayNonApprovalReasons() && _.isNil(selectedNonApprovalType)) {
      return "Please select a reason for status change";
    }

    if (isAdditionalCommentsRequired && !nonApprovalInfo) {
      return 'Please add additional info for "Other" reason';
    }

    return null;
  };

  const statusChangeHandler = (selected: any) => {
    const message =
      selected.value ===
      config.CONSTANTS.AUTHORIZATION_STATUSES.EDIT_AND_RESUBMIT
        ? strings.UPDATE_AUTH_STATUS.EDIT_AND_RESEND_NOTE
        : null;
    setSelectedOption(selected);
    setErrorMessage(message);
    resetFields();
  };

  const nonApprovalReasonChangeHandler = (selected: any) => {
    setSelectedNonApprovalType(selected);
    if (!shouldDisplayAdditionalComments()) {
      setNonApprovalInfo("");
    }
  };

  const getAdditionalComments = () => {
    if (!shouldDisplayAdditionalComments()) {
      return null;
    }

    return (
      <FieldContainer>
        <TextField
          label="Additional comments"
          multiline
          fullWidth
          value={nonApprovalInfo}
          required={isAdditionalCommentsRequired}
          InputLabelProps={{
            shrink: true,
            sx: {
              "& .MuiFormLabel-asterisk": {
                verticalAlign: "middle",
                color: theme.palette.error.main,
              },
            },
          }}
          inputProps={{
            maxLength: 500, // Maximum characters allowed
          }}
          onChange={(event) => {
            setNonApprovalInfo(event.target.value);
          }}
          helperText={`${nonApprovalInfo?.length ?? 0}/500 characters`}
        />
      </FieldContainer>
    );
  };

  const getReasonInputs = () => {
    if (shouldDisplayNonApprovalReasons()) {
      const nonApprovalReasonSelect = (
        <Autocomplete
          autoHighlight={true}
          options={getNonApprovalTypesOptions()}
          getOptionLabel={(option) => option.label}
          value={selectedNonApprovalType}
          renderInput={(params) => (
            <TextField
              {...params}
              required={shouldDisplayNonApprovalReasons()}
              InputLabelProps={{
                shrink: true,
                sx: {
                  "& .MuiFormLabel-asterisk": {
                    verticalAlign: "middle",
                    color: theme.palette.error.main,
                  },
                },
              }}
              label="Reason"
            />
          )}
          onChange={(event, newValue) => {
            nonApprovalReasonChangeHandler(newValue);
          }}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          filterOptions={(options, { inputValue }) => {
            return options.filter((option) =>
              option.label.toLowerCase().includes(inputValue.toLowerCase())
            );
          }}
        />
      );

      const followUpTypes = _.values(config.CONSTANTS.FOLLOW_UP_TYPES);
      const nextStepOptions = _.map(followUpTypes, (selection) => ({
        label: authorizationStatusDisplayName(selection),
        value: selection,
      }));

      const nextStepsSelect = (
        <Autocomplete
          autoHighlight={true}
          options={nextStepOptions}
          getOptionLabel={(option) => option.label}
          value={selectedFollowUpType}
          renderInput={(params) => (
            <TextField
              {...params}
              InputLabelProps={{
                shrink: true,
              }}
              label="Recommended next step"
            />
          )}
          onChange={(event, selected) => {
            setSelectedFollowUpType(selected);
          }}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          filterOptions={(options, { inputValue }) => {
            return options.filter((option) =>
              option.label.toLowerCase().includes(inputValue.toLowerCase())
            );
          }}
        />
      );

      const responseDueByPicker = (
        <DatePicker
          label="Response due by"
          name="updateAuthorizationStatus-responseDueBy"
          value={responseDueBy}
          slotProps={{
            textField: {
              fullWidth: true,
              InputLabelProps: {
                shrink: true,
              },
            },
          }}
          onChange={(newValue) => {
            setResponseDueBy(newValue);
          }}
        />
      );

      return (
        <>
          <FieldContainer>{nonApprovalReasonSelect}</FieldContainer>
          {paBoostEnabled && account?.isSamaUser && (
            <>
              {_.isNil(authorization?.PortalId) && (
                <FieldContainer>{nextStepsSelect}</FieldContainer>
              )}
              <FieldContainer>{responseDueByPicker}</FieldContainer>
            </>
          )}
          {getAdditionalComments()}
        </>
      );
    }
    return null;
  };

  const updateStatus = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (getErrorMessage()) {
      setErrorMessage(getErrorMessage());
    } else {
      try {
        if (
          portalAuthorizationId !== originalPortalAuthorizationId &&
          originalOption === (selectedOption.value as AuthorizationStatusEnum)
        ) {
          // Only updating portalAuthorizationId (status is unchanged)
          await updateAuthorization({
            variables: {
              id: parseInt(authorization.id),
              patch: {
                portalAuthorizationId,
                identifierCaptureType: IdentifierCaptureType.UserDirect,
              },
            },
          });
        } else {
          await updateAuthorizationStatus({
            variables: {
              id: parseInt(authorization.id),
              status: selectedOption.value as AuthorizationStatusEnum,
              nonApprovalInfo,
              nonApprovalType: selectedNonApprovalType?.value,
              responseDueBy,
              terminalStatusUpdateType: TerminalStatusUpdateType.User,
              ...(portalAuthorizationId === originalPortalAuthorizationId
                ? {}
                : {
                    portalAuthorizationId,
                    identifierCaptureType: IdentifierCaptureType.UserDirect,
                  }),
            },
          });

          if (selectedFollowUpType) {
            await createFollowUp({
              variables: {
                authorizationId: parseInt(authorization.id),
                type: selectedFollowUpType.value,
              },
            });
          }
        }

        setOpen(false);
        if (onUpdateSuccess) {
          onUpdateSuccess();
        }
      } catch (err) {
        alert.error("There was an error updating the Authorization");
      }
    }
  };

  const isUpdateDisabled = () => {
    if (
      selectedOption.value === "" ||
      (selectedOption.value as AuthorizationStatusEnum) === originalOption
    ) {
      // if the selected option matches the original one then disable submit
      if (!_.isNil(authorization.PortalId)) {
        return portalAuthorizationId === originalPortalAuthorizationId;
      }
      return true;
    }
    // logic to determine if update is disabled is the same as getErrorMessage
    return getErrorMessage() !== null;
  };

  let allowedStatuses = _.without(
    config.CONSTANTS.AUTHORIZATION_STATUSES_ALLOWED_ON_UPDATES,
    config.CONSTANTS.AUTHORIZATION_STATUSES.EDIT_AND_RESUBMIT
  );

  if (authorization.PortalId) {
    allowedStatuses =
      config.CONSTANTS.PORTAL_AUTHORIZATION_STATUSES_ALLOWED_ON_UPDATES;
  }

  const statusUpdateOptions = _.map(
    _.reject(allowedStatuses, (status) => status === selectedOption.value),
    (option) => ({
      value: option,
      label: authorizationStatusDisplayName(option),
    })
  );

  const getContent = () => {
    return (
      <>
        <Autocomplete
          data-cy="controlUpdateAuthToStatus"
          autoHighlight={true}
          options={statusUpdateOptions}
          getOptionLabel={(option) => option.label}
          value={selectedOption}
          renderInput={(params) => (
            <TextField
              {...params}
              InputLabelProps={{ shrink: true }}
              label="Update the status of this authorization"
            />
          )}
          onChange={(event, newValue) => {
            statusChangeHandler(newValue);
          }}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          filterOptions={(options, { inputValue }) => {
            return options.filter((option) =>
              option.label.toLowerCase().includes(inputValue.toLowerCase())
            );
          }}
        />
        {!_.isNil(authorization.PortalId) && (
          <FieldContainer>
            <TextField
              label="Portal authorization ID"
              fullWidth
              value={portalAuthorizationId}
              InputLabelProps={{
                shrink: true,
              }}
              onChange={(event) => {
                setPortalAuthorizationId(event.target.value);
              }}
            />
          </FieldContainer>
        )}
        {getReasonInputs()}
        {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
      </>
    );
  };

  const getActions = () => {
    const bgColor = isUpdateDisabled()
      ? theme.palette.divider
      : theme.palette.primary.main;
    return (
      <>
        <Button
          sx={{
            textDecoration: "underline",
            "&:hover": {
              background: "transparent",
            },
          }}
          onClick={toggleModal}
        >
          Cancel
        </Button>
        <Button
          sx={{
            borderRadius: "20px",
            background: bgColor,
            color: "white",
            "&:hover": {
              background: bgColor,
              cursor: isUpdateDisabled() ? "not-allowed" : "pointer",
            },
          }}
          onClick={updateStatus}
          disabled={isUpdateDisabled()}
          data-cy="actionUpdateAuthUpdateDetails"
        >
          Save
        </Button>
      </>
    );
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <DialogModal
        open={open}
        onClose={toggleModal}
        title="Update authorization status"
        content={getContent()}
        actions={getActions()}
        divider
        preventDefault
      />

      <Tooltip
        title="Manually update this authorization status"
        placement={tooltipPlace ?? "top"}
      >
        <BaseButton
          style={{
            padding: "3px",
            fontSize: "12px",
            display: "inline-block",
            ...(styleOverrides || {}),
          }}
          onClick={toggleModal}
          data-cy="actionUpdateAuthStatus"
          data-tooltip-id="updateAuthorizationStatusButton"
          disabled={disableTooltip}
        >
          {children}
        </BaseButton>
      </Tooltip>
    </div>
  );
};
